/**
 * app-config.js
 * 应用配置模块，从package.json读取配置信息，并提供给应用使用
 */

// 导入package.json配置
import pkgConfig from '../../package.json';

// 判断当前环境
const isDevelopment = process.env.NODE_ENV === 'development';

// 获取API配置
const apiConfig = pkgConfig.config?.api || {};
const apiEnvConfig = isDevelopment ? apiConfig.development : apiConfig.production;

// 获取SignalR配置
const signalRConfig = pkgConfig.config?.signalR || {};
const signalREnvConfig = isDevelopment ? signalRConfig.development : signalRConfig.production;

// 获取AIS配置
const aisConfig = pkgConfig.config?.ais || {};

// 获取雷达配置
const radarConfig = pkgConfig.config?.radar || {};

// 获取告警配置
const alertsConfig = pkgConfig.config?.alerts || {};

// 获取视频/云台配置
const videoCfg = pkgConfig.config?.video || {};
const videoEnvConfig = isDevelopment ? (videoCfg.development || {}) : (videoCfg.production || {});

// 获取船舶配置
const shipConfig = pkgConfig.config?.ship || {};

/**
 * 构建SignalR服务器URL
 * @returns {string} 完整的服务器URL
 */
function buildSignalRServerUrl() {
  const { protocol, host, port, path } = signalREnvConfig;
  
  if (!host) {
    // 如果没有配置主机，则返回相对路径
    return path || '/notification-hub';
  }
  
  // 构建完整URL
  let url = `${protocol}://${host}`;
  if (port) {
    url += `:${port}`;
  }
  url += path;
  
  return url;
}

// 获取iframe配置
const iframeConfig = pkgConfig.config?.iframe || {};

// 导出配置
export default {
  // 当前环境
  isDevelopment,
  
  // API配置
  api: {
    baseURL: apiEnvConfig.baseURL || 'http://localhost:18080/api',
    timeout: apiEnvConfig.timeout || 10000
  },
  
  // iframe嵌入配置（用于第三方系统集成）
  iframe: {
    // 是否允许被iframe嵌入
    allowEmbedding: iframeConfig.allowEmbedding !== false, // 默认允许
    // 允许的父页面域名列表（空数组表示允许所有）
    allowedOrigins: iframeConfig.allowedOrigins || [],
    // 是否在iframe模式下隐藏某些UI元素（如登录按钮、导航等）
    hideNavInIframe: iframeConfig.hideNavInIframe || false
  },
  
  // SignalR配置
  signalR: {
    serverUrl: buildSignalRServerUrl(),
    options: {
      autoReconnect: true,
      maxReconnectAttempts: 999999,
      reconnectInterval: 3
    }
  },
  
  // AIS配置
  ais: {
    topic: aisConfig.topic || 'AIS-Data-Client',
    messageType: aisConfig.messageType || 'AIS-Message'
  },
  
  // 雷达配置
  radar: {
    topic: radarConfig.topic || 'Radar-Data-Client',
    messageType: radarConfig.messageType || 'Radar-Sample-Data'
  },

  // 告警配置
  alerts: {
    topic: alertsConfig.topic || 'alerts',
    messageType: alertsConfig.messageType || 'alert'
  },

  // 船舶配置
  ship: {
    displayRadius: shipConfig.displayRadius || 10, // 船舶显示半径（公里）
    maxShipsCount: shipConfig.maxShipsCount || 1000 // 最大显示船舶数量
  },

  // 视频/云台配置
  video: {
    apiBase: videoEnvConfig.apiBase || '', // 空表示同源(/api/video)，开发指向本地 PTZ 代理
    stepDurationMs: videoEnvConfig.stepDurationMs || 500, // 单击最小步进时长(ms)
    stepMagnitude: videoEnvConfig.stepMagnitude || 30,    // 方向步幅(-100~100)
    zoomMagnitude: videoEnvConfig.zoomMagnitude || 30     // 变倍步幅
  },
  
  // 用于测试配置是否生效
  getFullConfig: () => {
    return {
      environment: process.env.NODE_ENV,
      api: apiEnvConfig,
      signalR: {
        serverUrl: buildSignalRServerUrl(),
        rawConfig: signalREnvConfig
      },
      ais: aisConfig,
      radar: radarConfig,
      alerts: alertsConfig,
      ship: shipConfig,
      iframe: iframeConfig
    };
  }
}; 